import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/ActivityDetail.css";
import { formatDate, formatDateTime } from "../helpers/dateFormat";
import { formatRupiah } from "../utils/formatRupiah";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";

const ActivityDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchActivityDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/showget-activities/${id}`,
          {
            headers: {
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          },
        );

        if (response.data.success) {
          setActivity(response.data.data);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError("Failed to fetch activity details.");
      } finally {
        setLoading(false);
      }
    };

    fetchActivityDetails();
  }, [id, i18n.language, t]);

  if (loading)
    return (
      <div className="activity-detail-container">
        <div className="shimmer-activity-detail shimmer-title"></div>
        <div className="shimmer-activity-detail shimmer-text"></div>
        <div className="shimmer-activity-detail shimmer-text"></div>
      </div>
    );

  if (error)
    return (
      <div className="error-container">
        <p className="error-text">{error}</p>
      </div>
    );

  return (
    <div className="activity-detail-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        <FaArrowLeft /> {t("back")}
      </button>

      <h2 className="activity-title">{activity.room_type.name}</h2>
      <h3 className="activity-subtitle">{activity.booking_reference}</h3>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          {t("overview")}
        </button>
        <button
          className={`tab-button ${activeTab === "room" ? "active" : ""}`}
          onClick={() => setActiveTab("room")}
        >
          {t("roomDetail")}
        </button>
        <button
          className={`tab-button ${activeTab === "guest" ? "active" : ""}`}
          onClick={() => setActiveTab("guest")}
        >
          {t("guestInfo")}
        </button>
        <button
          className={`tab-button ${activeTab === "payments" ? "active" : ""}`}
          onClick={() => setActiveTab("payments")}
        >
          {t("payment")}
        </button>
      </div>
      <div className="tab-content">
        {activeTab === "overview" && (
          <div className="overview">
            <p>
              <strong>Check-in:</strong> {activity.check_in_date}
            </p>
            <p>
              <strong>Check-out:</strong> {activity.check_out_date}
            </p>
            <p>
              <strong>{t("numberOfGuest")}:</strong> {activity.number_of_guests}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span className={`status-badge ${activity.status}`}>
                {activity.status.replace("_", " ").toUpperCase()}
              </span>
            </p>
            <p>
              <strong>{t("payment")}:</strong>{" "}
              <span className={`payment-badge ${activity.status_payment}`}>
                {activity.status_payment.replace("_", " ").toUpperCase()}
              </span>
            </p>
            {activity.notes && (
              <p>
                <strong>{t("note")}:</strong> {activity.notes}
              </p>
            )}
          </div>
        )}

        {activeTab === "room" && (
          <div className="room-details">
            <h3>
              {t("roomType")}: {activity.room_type.name}
            </h3>
            <p>Room Capacity: {activity.room_type.capacity} guests</p>
            <div className="photo-gallery">
              {activity.room_type.photos.map((photo, index) => (
                <img
                  key={index}
                  src={`${process.env.REACT_APP_STORAGE_URL}/${photo.photo_url}`}
                  alt={`Room ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === "guest" && activity.guest && (
          <div className="guest-info">
            <h3>
              {t("guestName")}: {activity.guest.full_name}
            </h3>
            <p>
              {t("phoneNumber")}: {activity.guest.phone_number}
            </p>
          </div>
        )}

        {activeTab === "payments" && (
          <div className="payments">
            {activity.payments.length > 0 ? (
              <table className="payment-table">
                <thead>
                  <tr>
                    <th>{t("paymentMethod")}</th>
                    <th>{t("amountPaid")}</th>
                    <th>Status</th>
                    <th>{t("date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.payments.map((payment, index) => (
                    <tr key={index}>
                      <td>{payment.payment_method}</td>
                      <td>{formatRupiah(payment.amount_paid)}</td>
                      <td>{payment.payment_status}</td>
                      <td>{formatDateTime(payment.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No payment records found.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityDetail;
